/*
 * Copyright © 2018-2023, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CountrySettingsResponse } from "../../models/management/CountrySettingsResponse";
import CountryApi from "../../api/CountryApi";
import { getGeneralError } from "../../util/helperFunctions";
import LoaderInline from "../../components/LoaderInline";
import AlertBox from "../../components/AlertBox";
import { strings } from "../../common/Strings/Strings";
import Button from "../../components/Button";
import { ChevronRight } from "../../common/Icons/ChevronRight";

interface CountryMenuItemProps {
  countryName: string;
  countryCode: string;
}

export const CountryMenuItem = (props: CountryMenuItemProps): ReactElement => {
  const { countryName, countryCode } = props;

  return (
    <Link to={`/management/countries/${countryCode}`}>
      <div className="flex p-3" key={countryCode}>
        <div className="">{countryName}</div>
        <div className="ml-auto">
          <Button variant="icon">
            <ChevronRight />
          </Button>
        </div>
      </div>
    </Link>
  );
};

interface Props {
  isActiveTab: boolean;
}

export default function CountrySection({ isActiveTab }: Props): ReactElement {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [countries, setCountries] = useState<CountrySettingsResponse[]>([]);
  const [error, setError] = useState<string | null>(null);

  const getCountrySettings = async () => {
    try {
      const resp = await CountryApi.getCountrySettings();
      setCountries(resp.data);
      setLoading(false);
    } catch (err) {
      setError(await getGeneralError(err));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isActiveTab) return;
    void getCountrySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveTab]);

  return (
    <>
      <LoaderInline className="my-3" hidden={!isLoading} />
      <div hidden={isLoading || error == null}>
        <AlertBox message={error} className="my-3" />
      </div>
      <div hidden={isLoading || error !== null}>
        <div className="flex flex-col items-start md:items-center justify-center space-y-6 mx-auto px-4 md:px-0">
          <div className="w-full md:max-w-md space-y-6">
            <div className="tw-card flex flex-col">
              <div className="flex p-3 items-center">
                <div className="font-bold">{strings.activeCountries}</div>
              </div>
              <div className="divide-y dark:divide-gray-700">
                {countries
                  .filter((i) => i.globalvetEnabled)
                  .map((country) => (
                    <div key={country.id}>
                      <CountryMenuItem countryName={country.countryName} countryCode={country.countryCode} />
                    </div>
                  ))}
              </div>
            </div>
            <div className="tw-card flex flex-col">
              <div className="flex p-3 items-center">
                <div className="font-bold">{strings.inactiveCountries}</div>
              </div>
              <div className="divide-y dark:divide-gray-700">
                {countries
                  .filter((i) => !i.globalvetEnabled)
                  .map((country) => (
                    <div key={country.id}>
                      <CountryMenuItem countryName={country.countryName} countryCode={country.countryCode} />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
